import { getReportFooterText } from '../reportFooter';
export interface PrimaryRestockItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  onHandQuantity?: number | null;
  minPrimaryQty?: number | null;
  restockQty?: number | null;
  qtyInStorage?: number | null;
  storageSite?: string | null;
  storageLocation?: string | null;
  storageSublocation?: string | null;
}

export interface PrimaryRestockTemplateOptions {
  generatedAt: Date;
  items: PrimaryRestockItem[];
  primaryLocationLabel?: string;
  secondaryLocationLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: PrimaryRestockItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="10" class="empty-row">
          No restock suggestions available. Ensure thresholds and stock levels are configured.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${formatNumber(item.onHandQuantity ?? null)}</td>
          <td class="numeric">${formatNumber(item.minPrimaryQty ?? null)}</td>
          <td class="numeric">${formatNumber(item.restockQty ?? null)}</td>
          <td class="numeric">${formatNumber(item.qtyInStorage ?? null)}</td>
          <td>${sanitizeText(item.storageSite)}</td>
          <td>${sanitizeText(item.storageLocation)}</td>
          <td>${sanitizeText(item.storageSublocation)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildPrimaryRestockReportHtml = ({
  generatedAt,
  items,
  primaryLocationLabel = 'Primary',
  secondaryLocationLabel = 'Secondary',
}: PrimaryRestockTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const itemsTable = buildItemsTable(items);
  const reportTitle = `${escapeHtml(primaryLocationLabel)} Restock Report`;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${reportTitle}</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 12px;
      }

      .title {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .generated {
        margin-top: 4px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 8px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 8px 10px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 8px 10px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 16px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">${reportTitle}</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="numeric">${escapeHtml(primaryLocationLabel)} Quantity</th>
            <th class="numeric">Min ${escapeHtml(primaryLocationLabel)} Qty</th>
            <th class="numeric">Restock</th>
            <th class="numeric">${escapeHtml(secondaryLocationLabel)} Quantity</th>
            <th>${escapeHtml(secondaryLocationLabel)} Site</th>
            <th>${escapeHtml(secondaryLocationLabel)} Location</th>
            <th>${escapeHtml(secondaryLocationLabel)} Sublocation(s)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
